import {
  Briefcase,
  CaretRight,
  Gear,
  Gift,
  Lightbulb,
  Question,
  SignOut,
} from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";

import RelativeLink from "common/components/RelativeLink";
import RenderIf from "common/components/RenderIf";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { DropdownMenuHeader } from "common/components/ui/DropdownMenu/DropdownMenu";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomer from "common/datahooks/useCustomer";
import { getUserback } from "common/helpers/userbackClient";
import { getWhitelabelCustomization } from "common/helpers/utils";

import LogOutModal from "../LogOutModal";

export default function ProfileDropdown({
  isExpanded,
}: {
  isExpanded: boolean;
}) {
  const { role } = useSelectedWorkspaceContext();
  const [isLogOutModalOpen, setIsLogOutModalOpen] = useState(false);

  const { customerInfo } = useCustomer();
  const { first_name: firstName, last_name: lastName, email } = customerInfo;
  const fullName = `${firstName} ${lastName}`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={clsx(
            "flex items-center gap-x-1.5 ml-1 mr-0.5 my-2 group overflow-hidden p-1.5 justify-start border border-transparent rounded-2xl transition-[background,border]",
            "hover:bg-black-100 hover:border-black-200",
            "data-[state=open]:bg-purple-50 data-[state=open]:border-purple-50 data-[state=open]:text-purple-600",
          )}
          data-cy="profile-button"
        >
          <AimfoxAvatar firstName={firstName} lastName={lastName} size="xs" />

          <span
            className={clsx(
              "transition-opacity duration-500 w-full truncate text-left text-xs-bold",
              isExpanded ? "opacity-100" : "opacity-0",
            )}
          >
            {fullName}
          </span>
          <CaretRight className="text-black-500 size-4 group-data-[state=open]:opacity-0 transition-opacity" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="z-tooltip w-[211px]"
          side="right"
          align="end"
        >
          {/* Profile Info */}
          <DropdownMenuHeader className="flex items-center gap-x-1.5">
            <AimfoxAvatar firstName={firstName} lastName={lastName} size="sm" />

            <div className="flex flex-col items-start min-w-0">
              <span className="w-full truncate text-left text-xs-bold">
                {firstName} {lastName}
              </span>
              <span className="w-full truncate text-button-xxs text-black-500">
                {email}
              </span>
            </div>
          </DropdownMenuHeader>

          <DropdownMenuList>
            <DropdownMenuItem asChild size="sm">
              <RelativeLink to="settings">
                <Gear weight="fill" />
                Settings
              </RelativeLink>
            </DropdownMenuItem>
            <RenderIf condition={!getWhitelabelCustomization()}>
              <RenderIf condition={role === "owner"}>
                <DropdownMenuItem asChild size="sm">
                  <RelativeLink to="settings/agency">
                    <Briefcase weight="fill" />
                    Agency plan
                  </RelativeLink>
                </DropdownMenuItem>
              </RenderIf>

              {/* Extra items */}
              {window.Cello && (
                <DropdownMenuItem
                  size="sm"
                  onClick={() => window.Cello("open")}
                  data-target="cello-button"
                >
                  <Gift weight="fill" />
                  Referral program
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => getUserback().openForm()}
                size="sm"
              >
                <Lightbulb weight="fill" />
                Feedback & Roadmap
              </DropdownMenuItem>
            </RenderIf>
            <DropdownMenuItem asChild size="sm">
              <a
                href={
                  getWhitelabelCustomization()
                    ? "https://help.lnconnect.me/en/"
                    : "https://help.aimfox.com/en/"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Question weight="fill" />
                Knowledge base
              </a>
            </DropdownMenuItem>
          </DropdownMenuList>
          <DropdownMenuFooter>
            <DropdownMenuItem
              size="sm"
              onClick={() => setIsLogOutModalOpen(true)}
              data-cy="log-out-button"
            >
              <SignOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuFooter>
        </DropdownMenuContent>
      </DropdownMenu>

      <LogOutModal
        isOpen={isLogOutModalOpen}
        setIsOpen={setIsLogOutModalOpen}
      />
    </>
  );
}
